import {
  BettingHotBetFilled,
  GeneralHelpCircle,
} from "@fanduel/formation-react-icons";
import cx from "classnames";
import { Button, Icon, Tag } from "@/components/formation";
import { SPORTSBOOK_BET_SELECTED, SPORTSBOOK_URL } from "@/constants/amplitude";
import { getFormattedWeekdayWithTimeAndTimezone } from "@/utils/date";
import { useAmplitude } from "@/utils/hooks/useAmplitude";
import { useHeadToHeadContext } from "@/utils/hooks/useHeadToHeadContext";
import { filterEmpty } from "@/utils/index";
import { formatOdds } from "@/utils/scores";
import TimeStampClient from "@/utils/TimeStampClient";
import type { Maybe, PopularBet } from "@/graphql/gql/graphql";
import styles from "./parlayContainer.module.scss";

export const formatWinAmount = (odds?: Maybe<number>): string => {
  if (typeof odds !== "number") {
    return "-";
  }
  if (odds > 0) {
    return (odds / 10).toFixed(2);
  }
  if (odds < 0) {
    return (1000 / Math.abs(odds)).toFixed(2);
  }
  return "-";
};

interface LegProps {
  runnerName: string;
  marketName?: string;
}

const Leg = ({ runnerName, marketName }: LegProps) => {
  return (
    <div className={styles.leg}>
      <div className={styles.dots}>
        <div className={styles.dotBorder} />
        <div className={styles.dot} />
        <div className={styles.dotBorder} />
      </div>
      <div className={styles.marketInfo}>
        <h4 className={styles.marketRunnerName}>{runnerName}</h4>
        <span className={styles.marketName}>{marketName}</span>
      </div>
    </div>
  );
};

interface ParlayContainerProps {
  toggleModal: () => void;
  isModal?: boolean;
  addToBetslip: (url: string) => void;
  parlay: PopularBet;
}

const ParlayContainer = ({
  addToBetslip,
  toggleModal,
  isModal,
  parlay,
}: ParlayContainerProps) => {
  const { logAmplitudeEvent } = useAmplitude();
  const { eventName } = useHeadToHeadContext();
  const betUrl = parlay?.betUrl ?? SPORTSBOOK_URL;
  const count = parlay?.count ?? 0;
  const description = `$10 to win $${formatWinAmount(parlay?.americanOdds)}`;
  const parlayType = parlay?.type ?? "";
  const parlayMarket = parlay?.events?.[0];
  const openDate = parlayMarket?.openDate ?? "";
  const formattedOdds = formatOdds(parlay?.americanOdds);
  const maybeSelections = parlayMarket?.selections ?? [];
  const selections = maybeSelections ? filterEmpty(maybeSelections) : [];
  const selectionsLength = selections?.length ?? 0;

  const handleAddToBetslip = () => {
    if (isModal) {
      toggleModal();
    }
    addToBetslip(betUrl);
    logAmplitudeEvent(SPORTSBOOK_BET_SELECTED, {
      "Event Name": eventName,
      Module: "popular_bets",
      "Link URL": betUrl,
      "Link Text": description,
      "Bet Type": parlayType,
      "Market Selection Name": JSON.stringify(selections),
      "Page State": "pre_game",
    });
  };

  return (
    <div className={cx(styles.container, isModal && styles.modal)}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h3>
            {`${selectionsLength} Leg Parlay `}
            <GeneralHelpCircle />
          </h3>
          <div className={styles.time}>
            <TimeStampClient
              formatter={getFormattedWeekdayWithTimeAndTimezone}
              isoString={openDate}
            />
          </div>
        </div>
        <div>
          <span className={styles.headerOdds}>{formattedOdds}</span>
        </div>
      </div>
      <div className={styles.contentWrapper}>
        <div className={styles.content}>
          {selections?.map((selection, index) => {
            const marketName = selection?.marketName ?? "Market";
            const runnerName = selection?.runnerName ?? "";
            return (
              <Leg
                key={`${index}-${marketName}-${runnerName}`}
                marketName={selection?.marketName ?? "Market"}
                runnerName={selection?.runnerName ?? ""}
              />
            );
          })}
        </div>
        <div className={styles.ctaWrapper}>
          <div className={styles.tagWrapper}>
            <Tag intent="insight" variant="fill">
              <Icon as={BettingHotBetFilled} /> {`${count} People Placed`}
            </Tag>
          </div>
          <Button
            label="Add to Betslip"
            variant="primary"
            description={description}
            fill
            onClick={handleAddToBetslip}
          />
          {isModal && (
            <Button onClick={toggleModal} label="Maybe Later" variant="link" />
          )}
        </div>
      </div>
    </div>
  );
};

export default ParlayContainer;
